import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, Phone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContactFormSection } from '@/components/contact/contact-form-section';
import { useUser } from '@/hooks/use-user';
import { type Tenant, useTenant } from '@/providers/tenant-context';
import { toCssHsl } from '@/lib/brand-theme';
import { MiloraLandingContent } from '@/pages/landing/milora-landing-content';
import { TenantFaqSection } from '@/components/tenant-faq/tenant-faq-section';
import {
  LANDING_SECTION_IDS,
  SALES_PHONE_DISPLAY,
  scrollToSection,
} from '@/pages/landing/constants';

const NAV_LINKS = [
  { label: 'Product', id: LANDING_SECTION_IDS.product },
  { label: 'How it works', id: LANDING_SECTION_IDS.howItWorks },
  { label: 'Use cases', id: LANDING_SECTION_IDS.useCases },
  { label: 'FAQ', id: LANDING_SECTION_IDS.faq },
] as const;

type LandingTemplateContext = Record<string, unknown>;

function escapeTemplateValue(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getTemplateValue(context: LandingTemplateContext, path: string): unknown {
  return path.split('.').reduce<unknown>((value, key) => {
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, context);
}

function renderConditionalBlocks(html: string, context: LandingTemplateContext) {
  let rendered = html;
  let previous = '';

  while (previous !== rendered) {
    previous = rendered;
    rendered = rendered
      .replace(/{{#if\s+([\w.]+)}}([\s\S]*?){{\/if}}/g, (_match, path, content) =>
        getTemplateValue(context, path) ? content : ''
      )
      .replace(/{{#unless\s+([\w.]+)}}([\s\S]*?){{\/unless}}/g, (_match, path, content) =>
        getTemplateValue(context, path) ? '' : content
      );
  }

  return rendered;
}

function filterVisibilityAttributes(html: string, isAuthenticated: boolean) {
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<div data-root>${html}</div>`, 'text/html');
  const root = doc.body.querySelector('[data-root]');
  if (!root) return html;

  root.querySelectorAll('[data-milora-show-when]').forEach((element) => {
    const value = element.getAttribute('data-milora-show-when');
    const show =
      (value === 'authenticated' && isAuthenticated) || (value === 'guest' && !isAuthenticated);

    if (show) {
      element.removeAttribute('data-milora-show-when');
    } else {
      element.remove();
    }
  });

  return root.innerHTML;
}

function renderTenantLandingHtml(html: string, context: LandingTemplateContext) {
  const rendered = renderConditionalBlocks(html, context).replace(
    /{{\s*([\w.]+)\s*}}/g,
    (_match, path) => escapeTemplateValue(getTemplateValue(context, path))
  );

  return filterVisibilityAttributes(rendered, Boolean(context.isAuthenticated));
}

function buildLandingTemplateContext(tenant: Tenant | null, isAuthenticated: boolean) {
  return {
    isAuthenticated,
    isGuest: !isAuthenticated,
    loginUrl: '/login',
    dashboardUrl: '/dashboard',
    contactUrl: '/contact',
    tenant: {
      id: tenant?.id ?? '',
      name: tenant?.name ?? '',
      slug: tenant?.slug ?? '',
      logo: tenant?.logo ?? '',
      logoWithName: tenant?.logoWithName ?? '',
      banner: tenant?.banner ?? '',
      primaryColor: toCssHsl(tenant?.themeColors),
    },
  };
}

export default function LandingPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isLoading: isUserLoading } = useUser();
  const isAuthenticated = Boolean(user);
  const { tenant, isWhitelabel, isLoading } = useTenant();
  const navigate = useNavigate();
  const hasCustomLanding = !isLoading && isWhitelabel && !!tenant?.landingPageHtml;
  const customLandingHtml = useMemo(() => {
    if (!hasCustomLanding || !tenant?.landingPageHtml) return null;
    return renderTenantLandingHtml(
      tenant.landingPageHtml,
      buildLandingTemplateContext(tenant, isAuthenticated)
    );
  }, [hasCustomLanding, isAuthenticated, tenant]);

  useEffect(() => {
    if (!isLoading && !isUserLoading && isWhitelabel && tenant?.redirectToLogin) {
      if (!isAuthenticated) {
        navigate('/login', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isLoading, isUserLoading, isWhitelabel, tenant, isAuthenticated, navigate]);

  const handleNavClick = (id: string) => {
    scrollToSection(id);
    setIsMobileMenuOpen(false);
  };

  if (isLoading) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-background relative overflow-x-hidden transition-colors duration-300 font-sans">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="fixed inset-0 w-full h-full -z-10 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] pointer-events-none" />

      <header className="fixed top-0 w-full z-50 border-b border-border/10 bg-background/60 backdrop-blur-xl">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link className="flex items-center gap-2 group" to="/">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-10 h-10 object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <span className="font-bold text-2xl tracking-tighter text-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </Link>

          <nav className="hidden lg:flex gap-6 items-center">
            {!hasCustomLanding &&
              NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => handleNavClick(link.id)}
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.label}
                </button>
              ))}
            <Link
              to="/contact"
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              Contact
            </Link>
            <div className="h-6 w-px bg-border/40" />
            {!hasCustomLanding && (
              <Button
                variant="outline"
                className="rounded-full px-4"
                onClick={() => scrollToSection(LANDING_SECTION_IDS.contact)}
              >
                Request Demo
              </Button>
            )}
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="rounded-full px-6 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all">
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  to="/login"
                >
                  Login
                </Link>
                <Link to="/login">
                  <Button className="rounded-full px-6 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </nav>

          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden absolute top-20 left-0 w-full bg-background border-b border-border p-6 flex flex-col gap-4 shadow-xl"
          >
            {!hasCustomLanding &&
              NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  className="text-lg font-medium text-left"
                  onClick={() => handleNavClick(link.id)}
                >
                  {link.label}
                </button>
              ))}
            <Link
              to="/contact"
              className="text-lg font-medium"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact
            </Link>
            {!hasCustomLanding && (
              <button
                type="button"
                className="text-lg font-medium text-left flex items-center gap-2"
                onClick={() => handleNavClick(LANDING_SECTION_IDS.contact)}
              >
                <Phone className="h-4 w-4" />
                Request Demo · {SALES_PHONE_DISPLAY}
              </button>
            )}
            <hr className="border-border/50" />
            {isAuthenticated ? (
              <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full">Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full">Get Started</Button>
                </Link>
              </>
            )}
          </motion.div>
        )}
      </header>

      <main className="flex-1 pt-20">
        {hasCustomLanding && customLandingHtml ? (
          <div data-tenant-landing-html dangerouslySetInnerHTML={{ __html: customLandingHtml }} />
        ) : (
          <MiloraLandingContent isAuthenticated={isAuthenticated} />
        )}
        {isWhitelabel && <TenantFaqSection />}
        <ContactFormSection variant={hasCustomLanding ? 'default' : 'demo'} />
      </main>

      <footer className="py-12 px-6 border-t border-border/40 bg-muted/20">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-8 w-auto object-contain grayscale opacity-80"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-8 h-8 object-contain grayscale opacity-80"
                />
                <span className="font-bold text-lg text-muted-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </div>
          <nav className="flex flex-wrap gap-4 md:gap-8 justify-center">
            <Link
              className="text-sm text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              to="/contact"
            >
              Contact
            </Link>
            <Link
              className="text-sm text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              to="/privacy"
            >
              Privacy
            </Link>
            <Link
              className="text-sm text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              to="/terms"
            >
              Terms
            </Link>
            <Link
              className="text-sm text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              to="/csae"
            >
              CSAE Standards
            </Link>
          </nav>
          <div className="flex flex-col items-center md:items-end gap-1 text-sm text-muted-foreground/60">
            <p>
              © {new Date().getFullYear()}{' '}
              <a
                href="https://simpliumtechnologies.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline"
              >
                Simplium Technologies LLP
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
